// Date helpers that survive whatever the API decides to hand us back.
//
// The VitalEngine API normally returns dates as ISO strings, but a handful of
// records in the cache come through as a Salesforce-style date/datetime
// object that looks like:
//
//   { isValidDateTime, year, month, day, hour, minute, second,
//     millisecond, microsecond, isNull, value }
//
// React 18 throws "Objects are not valid as a React child" (#31) the moment
// we try to render one of those directly, even though `new Date(obj)` just
// silently produces an Invalid Date. Every component that puts a date into
// JSX needs to coerce it first; these helpers are that coercion.

// Pull a usable string|number|Date out of an unknown date-shaped value.
// Returns null when nothing usable is present (callers can then render
// their own placeholder like '—').
export function unwrapDateLike(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'string' || typeof v === 'number') return v;
  if (v instanceof Date) return v;
  if (typeof v === 'object') {
    if (v.isNull === true) return null;
    // SF date objects almost always carry the canonical ISO string in `value`.
    if (typeof v.value === 'string' || typeof v.value === 'number') return v.value;
    // Fall back to assembling from components when `value` isn't there.
    if (Number.isFinite(v.year) && Number.isFinite(v.month) && Number.isFinite(v.day)) {
      const yyyy = String(v.year).padStart(4, '0');
      const mm   = String(v.month).padStart(2, '0');
      const dd   = String(v.day).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
  }
  return null;
}

// Always-safe printable date string. Returns '' (not "Invalid Date") when
// the input can't be coerced — the caller decides how to placeholder.
export function safeDateString(v, opts = {}) {
  const raw = unwrapDateLike(v);
  if (raw == null) return '';
  // Strings already in YYYY-MM-DD form: render directly so the locale
  // formatter doesn't shift them by a timezone hour.
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    if (opts.locale === false) return raw;
    const [y, m, d] = raw.split('-');
    return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
  }
  const d = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return typeof raw === 'string' ? raw : '';
  }
  return opts.includeTime ? d.toLocaleString() : d.toLocaleDateString();
}

// Convenience: returns a millisecond timestamp suitable for sorting/binning,
// or 0 when the input is unusable. Mirrors what the existing call sites
// were doing with `new Date(t).getTime()` but works on SF date objects too.
export function dateMs(v) {
  const raw = unwrapDateLike(v);
  if (raw == null) return 0;
  const d = raw instanceof Date ? raw : new Date(raw);
  const t = d.getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Four-digit birth year for de-identified exports; '' when unknown. */
export function extractBirthYear(v) {
  const raw = unwrapDateLike(v);
  if (raw == null) return '';

  if (typeof raw === 'object' && raw !== null && Number.isFinite(raw.year)) {
    return String(raw.year);
  }

  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return '';
    const masked = s.match(/\*{2}\/\*{2}\/(\d{4})/);
    if (masked) return masked[1];
    const iso = s.match(/^(\d{4})-\d{2}-\d{2}/);
    if (iso) return iso[1];
    const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slash) return slash[3];
    const yearOnly = s.match(/^\d{4}$/);
    if (yearOnly) return s;
  }

  const d = raw instanceof Date ? raw : new Date(raw);
  if (!Number.isNaN(d.getTime())) return String(d.getFullYear());
  return '';
}
